/**
 * Shared utilities for calling Supabase Edge Functions.
 *
 * Uses supabase.functions.invoke() which handles auth automatically,
 * including support for both publishable keys and JWT anon keys.
 */

import { supabase } from './supabase';
import { FunctionsHttpError } from '@supabase/supabase-js';

export interface EdgeFunctionError {
  status: number;
  message: string;
  details?: string;
  [key: string]: unknown;
}

/**
 * Type guard for EdgeFunctionError.
 */
export function isEdgeFunctionError(err: unknown): err is EdgeFunctionError {
  return typeof err === 'object' && err !== null && 'status' in err && 'message' in err;
}

/**
 * Invoke a Supabase Edge Function using the official JS client.
 *
 * This handles authentication automatically (sends the user's JWT + apikey)
 * and works with both publishable keys and legacy JWT anon keys.
 *
 * @param functionName - The edge function name (e.g. 'credit-operations')
 * @param body - The request body (will be JSON-serialized)
 * @returns The parsed JSON response data
 * @throws EdgeFunctionError with status code and parsed error body on HTTP errors
 */
export async function invokeEdgeFunction<T = unknown>(
  functionName: string,
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error, response } = await supabase.functions.invoke(functionName, {
    body,
  });

  if (error) {
    if (error instanceof FunctionsHttpError && response) {
      const status = response.status;
      let errorBody: Record<string, unknown> = {};
      try {
        errorBody = await response.json();
      } catch {
        errorBody = { error: error.message };
      }
      const edgeError: EdgeFunctionError = {
        status,
        message: (errorBody.error as string) || error.message,
        details: errorBody.details as string | undefined,
        ...errorBody,
      };
      throw edgeError;
    }
    throw error;
  }

  return data as T;
}
